import type { TwiageCase } from "@/App/types/twiageTypes";
import { differenceInMinutes, format, intervalToDuration, minutesInHour } from "date-fns";
import { CaseType } from "@/App/types/caseType";

export const nameSort = (a: { name: string }, b: { name: string }): number => {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
};

export const calculateEtaAgeInMinutes = ({ twiageCase }: { twiageCase: TwiageCase }): string => {
  return ageOfTheDate(twiageCase.locationUpdates?.[0]?.createdAt ?? 0);
};

export const etaAge = ({ twiageCase, shortened }: { twiageCase: TwiageCase; shortened: boolean }): string => {
  if (!twiageCase.eta || twiageCase.eta === -1 || twiageCase.type === CaseType.hospital) {
    return "";
  }
  return `${shortened ? "upd." : "updated"} ${calculateEtaAgeInMinutes({ twiageCase })} ago`;
};

export const ageOfTheDate = (dateString: string): string => {
  let unit = "second";
  let timeAgo: string | number = "a few";
  const minutes = differenceInMinutes(new Date(), new Date(dateString));
  if (minutes >= minutesInHour * 24) {
    timeAgo = Math.round(minutes / minutesInHour / 24);
    unit = "day";
  } else if (minutes >= minutesInHour) {
    timeAgo = Math.round(minutes / minutesInHour);
    unit = "hour";
  } else if (minutes > 1) {
    timeAgo = minutes;
    unit = "minute";
  }
  if (timeAgo !== 1) {
    unit += "s";
  }
  return `${timeAgo} ${unit}`;
};

export const readableDateTime = (timestamp?: string | Date | number, dateFormat: string = "hh:mm aa MM/dd"): string => {
  if (timestamp) {
    return format(timestamp instanceof Date ? timestamp : new Date(timestamp), dateFormat);
  }
  return "";
};

export const truncateWithEllipses = ({ string, maxLength }: { string: string; maxLength: number }): string => {
  return string.substring(0, maxLength - 1) + (string.length > maxLength ? "..." : "");
};

export const formatMaximumDisplayableDuration = ({ durationInSeconds }: { durationInSeconds: number }): string => {
  const duration = intervalToDuration({ start: 0, end: durationInSeconds * 1000 });

  // note that this is not going to work (give the correct amount of hours) for a duration of more than 30 days because that's the resolution of a date-fns duration
  // https://date-fns.org/v2.30.0/docs/intervalToDuration
  const hours = (duration.days || 0) * 24 + (duration.hours || 0);

  return `${hours}:${String(duration.minutes).padStart(2, "0")}:${String(duration.seconds).padStart(2, "0")}`;
};

export const addBreakOnSlash = (string: string): string => {
  return string.replace("/", "/\u200B");
};
